import apiFetch from "@wordpress/api-fetch";
import { useDispatch, useSelect } from "@wordpress/data";
import { __ } from "@wordpress/i18n";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import Button from "../../button";
import { transition } from "../auth-state-machine";
import { authStore } from "../auth-store";
import { applyNewNonce, handleApiErrorOrReload } from "../auth-utils";
import {
	defaultCopyForIntent,
	defaultSubmitTextForStage,
} from "../default-copy";

type Props = {
	copySubmitOverride?: string;
	accountCreationNoteOverride?: string;
};

export const CreateAccountStage: React.FC<Props> = ({
	copySubmitOverride,
	accountCreationNoteOverride,
}) => {
	const email = useSelect((select) => select(authStore).getEmail(), []);
	const intent = useSelect((select) => select(authStore).getIntent(), []);
	const error = useSelect((select) => select(authStore).getError(), []);
	const isSubmitting = useSelect(
		(select) => select(authStore).isSubmitting(),
		[],
	);
	const { setError, setSubmitting, setStage } = useDispatch(authStore);
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const abortRef = useRef<AbortController | null>(null);
	const note =
		accountCreationNoteOverride ??
		defaultCopyForIntent(intent).accountCreationNote;

	useEffect(() => () => abortRef.current?.abort(), []);

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setSubmitting(true);
		abortRef.current?.abort();
		const ac = new AbortController();
		abortRef.current = ac;
		try {
			const res = await apiFetch<{
				success: true;
				firstName: string;
				nonce?: string;
			}>({
				path: "/leat/v1/register-and-join",
				method: "POST",
				data: {
					email,
					...(firstName && { firstName }),
					...(lastName && { lastName }),
				},
				signal: ac.signal,
			});
			if (res.nonce) applyNewNonce(res.nonce);
			const next = transition(
				{ stage: "createAccount", email },
				{ type: "registerSuccess" },
				intent,
			);
			setStage(next.stage);
		} catch (err) {
			if ((err as { name?: string })?.name === "AbortError") return;
			const apiErr = err as { code?: string };
			if (apiErr?.code === "email_exists") {
				const next = transition(
					{ stage: "createAccount", email },
					{ type: "emailExistsError" },
					intent,
				);
				setStage(next.stage);
				return;
			}
			const msg = handleApiErrorOrReload(err);
			if (msg) setError(msg);
		} finally {
			if (!ac.signal.aborted) setSubmitting(false);
		}
	};

	return (
		<form onSubmit={onSubmit} noValidate>
			<div className="leat-auth-modal__field">
				<label htmlFor="leat-auth-fname">{__("First name", "leat-crm")}</label>
				<input
					id="leat-auth-fname"
					type="text"
					autoComplete="given-name"
					value={firstName}
					onChange={(e) => setFirstName(e.target.value)}
				/>
			</div>
			<div className="leat-auth-modal__field">
				<label htmlFor="leat-auth-lname">{__("Last name", "leat-crm")}</label>
				<input
					id="leat-auth-lname"
					type="text"
					autoComplete="family-name"
					value={lastName}
					onChange={(e) => setLastName(e.target.value)}
				/>
			</div>
			<div className="leat-auth-modal__field">
				<label htmlFor="leat-auth-email-readonly">
					{__("Email", "leat-crm")}
				</label>
				<input
					id="leat-auth-email-readonly"
					type="email"
					value={email}
					readOnly
				/>
				<button
					type="button"
					className="leat-auth-modal__back-link"
					onClick={() => setStage("emailEntry")}
				>
					{__("Use a different email", "leat-crm")}
				</button>
			</div>
			{note && <p className="leat-auth-modal__note">{note}</p>}
			{error && (
				<div className="leat-auth-modal__submit-error" role="alert">
					{error}
				</div>
			)}
			<Button
				type="submit"
				variant="contained"
				className="leat-auth-modal__submit leat-button--pill"
				disabled={isSubmitting}
			>
				{isSubmitting
					? __("Creating…", "leat-crm")
					: (copySubmitOverride ?? defaultSubmitTextForStage("createAccount"))}
			</Button>
		</form>
	);
};
